import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
axios.defaults.baseURL = API_URL;

export const authAPI = {
  login: (data) => axios.post('/auth/login', data),
  getMe: () => axios.get('/auth/me'),
  changePassword: (data) => axios.post('/auth/change-password', data),
  getUsers: () => axios.get('/auth/users'),
  createUser: (data) => axios.post('/auth/users', data),
  updateUser: (id, data) => axios.put(`/auth/users/${id}`, data),
  toggleUser: (id) => axios.patch(`/auth/users/${id}/toggle`),
  resetPassword: (id, data) => axios.post(`/auth/users/${id}/reset-password`, data),
  getPermissions: () => axios.get('/permissions'),
  updatePermission: (data) => axios.post('/permissions', data),
  getRoles: () => axios.get('/roles'),
  addRole: (data) => axios.post('/roles', data),
  deleteRole: (role) => axios.delete(`/roles/${role}`),
};

export const settingsAPI = {
  get: () => axios.get('/settings'),
  update: (data) => axios.put('/settings', data),
  uploadLogo: (form) => axios.post('/settings/logo', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => axios.delete('/settings/logo'),
  getAcademicYears: () => axios.get('/academic-years'),
  createAcademicYear: (data) => axios.post('/academic-years', data),
  setCurrentYear: (id) => axios.patch(`/academic-years/${id}/set-current`),
  updateAcademicYear: (id, data) => axios.put(`/academic-years/${id}`, data),
  deleteAcademicYear: (id) => axios.delete(`/academic-years/${id}`),
  getClasses: () => axios.get('/classes'),
  createClass: (data) => axios.post('/classes', data),
  updateClass: (id, data) => axios.put(`/classes/${id}`, data),
  deleteClass: (id) => axios.delete(`/classes/${id}`),
};

export const studentsAPI = {
  getAll: (params) => axios.get('/students', { params }),
  getById: (id) => axios.get(`/students/${id}`),
  getStats: () => axios.get('/students/stats'),
  getNextAdmission: () => axios.get('/students/next-admission'),
  create: (form) => axios.post('/students', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, form) => axios.put(`/students/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => axios.delete(`/students/${id}`),
};

export const teachersAPI = {
  getAll: (params) => axios.get('/teachers', { params }),
  getById: (id) => axios.get(`/teachers/${id}`),
  getStats: () => axios.get('/teachers/stats'),
  getMyAssignments: () => axios.get('/teachers/my-assignments'),
  getMyHomeClasses: () => axios.get('/teachers/my-home-classes'),
  create: (form) => axios.post('/teachers', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, form) => axios.put(`/teachers/${id}`, form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => axios.delete(`/teachers/${id}`),
  createAccount: (id, data) => axios.post(`/teachers/${id}/create-account`, data),
};

export const subjectsAPI = {
  getAll: (params) => axios.get('/subjects', { params }),
  getById: (id) => axios.get(`/subjects/${id}`),
  getByClass: (params) => axios.get('/subjects/by-class', { params }),
  getAllAssignments: (params) => axios.get('/class-assignments', { params }),
  create: (data) => axios.post('/subjects', data),
  update: (id, data) => axios.put(`/subjects/${id}`, data),
  delete: (id) => axios.delete(`/subjects/${id}`),
  assignToClass: (data) => axios.post('/subjects/assign-class', data),
};

export const enrolmentsAPI = {
  getAll: (params) => axios.get('/enrolments', { params }),
  getStats: (params) => axios.get('/enrolments/stats', { params }),
  enrol: (data) => axios.post('/enrolments', data),
  bulkEnrol: (data) => axios.post('/enrolments/bulk', data),
  update: (id, data) => axios.put(`/enrolments/${id}`, data),
};

export const assessmentsAPI = {
  getAll: (params) => axios.get('/assessments', { params }),
  getById: (id) => axios.get(`/assessments/${id}`),
  getSummary: (params) => axios.get('/assessments/summary', { params }),
  create: (data) => axios.post('/assessments', data),
  update: (id, data) => axios.put(`/assessments/${id}`, data),
  delete: (id) => axios.delete(`/assessments/${id}`),
};

export const marksAPI = {
  getByAssessment: (id) => axios.get(`/marks/assessment/${id}`),
  getByStudent: (id, params) => axios.get(`/marks/student/${id}`, { params }),
  getEntrySheet: (assessmentId, classId) => axios.get(`/marks/entry-sheet/${assessmentId}${classId ? '?class_id=' + classId : ''}`),
  save: (data) => axios.post('/marks', data),
  bulkSave: (data) => axios.post('/marks/bulk', data),
  delete: (id) => axios.delete(`/marks/${id}`),
};

export const scoresheetAPI = {
  getClass: (params) => axios.get('/scoresheet/class', { params }),
  getStudent: (params) => axios.get('/scoresheet/student', { params }),
};

export const passingCriteriaAPI = {
  getAll: () => axios.get('/passing-criteria'),
  save: (data) => axios.post('/passing-criteria', data),
  evaluate: (params) => axios.get('/passing-criteria/evaluate', { params }),
  getGradingScales: () => axios.get('/grading-scales'),
  createScale: (data) => axios.post('/grading-scales', data),
  saveBoundaries: (data) => axios.post('/grading-scales/boundaries', data),
  getGrade: (params) => axios.get('/grading-scales/grade', { params }),
};

export const reportsAPI = {
  getDashboard: (params) => axios.get('/reports/dashboard', { params }),
  getClassPerformance: (params) => axios.get('/reports/class-performance', { params }),
  getStudentProgress: (id) => axios.get(`/reports/student-progress/${id}`),
  getEnrolment: (params) => axios.get('/reports/enrolment', { params }),
  getTeacherWorkload: (params) => axios.get('/reports/teacher-workload', { params }),
  getPromotion: (params) => axios.get('/reports/promotion', { params }),
};

export const subjectStudentsAPI = {
  get: (params) => axios.get('/teacher-subject-students', { params }),
  save: (data) => axios.post('/teacher-subject-students', data),
  clear: (params) => axios.delete('/teacher-subject-students', { params }),
};

export const levelsAPI = {
  getAll: () => axios.get('/levels'),
  create: (data) => axios.post('/levels', data),
  update: (id, data) => axios.put(`/levels/${id}`, data),
  delete: (id) => axios.delete(`/levels/${id}`),
};

export const promotionRulesAPI = {
  getAll: () => axios.get('/promotion-rules'),
  save: (data) => axios.post('/promotion-rules', data),
  delete: (id) => axios.delete(`/promotion-rules/${id}`),
  evaluate: (params) => axios.get('/promotion-rules/evaluate', { params }),
};

export const attendanceAPI = {
  getRollCall:    (params) => axios.get('/attendance/roll-call', { params }),
  saveRollCall:   (data)   => axios.post('/attendance/roll-call', data),
  getStudent:     (params) => axios.get('/attendance/student', { params }),
  getClassSummary:(params) => axios.get('/attendance/class-summary', { params }),
  getDailyStats:  (params) => axios.get('/attendance/daily-stats', { params }),
};

export const parentAdminAPI = {
  listParents:    ()     => axios.get('/admin/parents'),
  toggleActive:   (id, is_active) => axios.patch(`/admin/parents/${id}`, { is_active }),
  deleteParent:   (id)   => axios.delete(`/admin/parents/${id}`),
};

export const auditAPI = {
  getLogs:    (params) => axios.get('/audit-logs', { params }),
  getStats:   () => axios.get('/audit-logs/stats'),
  getFilters: () => axios.get('/audit-logs/filters'),
};

export default { auth: authAPI, settings: settingsAPI, students: studentsAPI, teachers: teachersAPI, subjects: subjectsAPI, enrolments: enrolmentsAPI, assessments: assessmentsAPI, marks: marksAPI, scoresheet: scoresheetAPI, passingCriteria: passingCriteriaAPI, reports: reportsAPI, subjectStudents: subjectStudentsAPI, levels: levelsAPI, promotionRules: promotionRulesAPI, audit: auditAPI };
